import React, { ReactNode } from 'react'
import { applyColorMode } from '@airbus/elements/theming'
import { useTheme } from './ThemeProvider'

export const ColorModeContext = React.createContext(null)

type ColorModeProviderProps = {
  mode: string
  children: ReactNode
}

/* This component allows container components (such as a dark header) to provide a color mode to their children.
A color mode is a variant of a theme whose colors are adapted to a specific background color.
Thus, a color mode is indistinguishable from a theme to a component's POV */
export default function ColorModeProvider({ children, mode = 'default' }: ColorModeProviderProps) {
  const theme = useTheme()
  const colorMode = mode === 'default' ? theme : applyColorMode(theme, mode)
  return <ColorModeContext.Provider value={colorMode}>{children}</ColorModeContext.Provider>
}

/* This hook either returns the default theme or a color mode if a parent use a ColorModeProvider.
Therefore it must be used by all components instead of useTheme (and is used by createUseStyles anyway). */
export const useColorMode = () => {
  const outerMode = React.useContext(ColorModeContext)
  const theme = useTheme()
  return outerMode || theme
}
